import { compact, find, get, isEmpty, isEqual, keys, pickBy, some, sortBy } from 'lodash';

import { AttributesTable } from 'interfaces/inferenceArtifacts';
import PostProcessingActionInterface, {
  ActionInput,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import { OrchestrationMetadata } from '../classConfigFromSelections';
import { INSTANCE_COUNT_POSITIVITY_ACTION_NAME, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY } from './instanceCountPositivity';

// Instance H-Score is a feature_family action (belongs on the Features list), not a cell/area
// post-processing action, even though it consumes columns that post-processing actions produce.
export const INSTANCE_H_SCORE_ACTION_NAME = 'instance_h_score';
export const INSTANCE_H_SCORE_BINNED_COLUMNS_INPUT_KEY = 'action_inputs.binned_count_columns';

const SUGGESTED_ACTION_NAMES = [INSTANCE_COUNT_POSITIVITY_ACTION_NAME, INSTANCE_H_SCORE_ACTION_NAME];

// Instance H-Score/Positivity need both spot detection counts and a cell segmentation to bin them
// into; a stain with only one of the two cannot use either action yet, so it must not be suggested.
export const getStainsWithSpotDetectionAndCellSegmentation = (orchestrationMetadata: OrchestrationMetadata): string[] =>
  keys(
    pickBy(
      orchestrationMetadata,
      (byAttributesTable) =>
        !isEmpty(byAttributesTable?.[AttributesTable.DetectionsTable]) &&
        !isEmpty(byAttributesTable?.[AttributesTable.CellTable])
    )
  );

export interface SpotDetectionActionSuggestion {
  actionId: string;
  actionName: string;
  actionLabel: string;
  // Whether this action belongs on the Features list (featureActions) rather than the Post
  // Processing Actions list (postProcessingActions). Derived from the live registry's actionInput,
  // not hardcoded, so it can't silently drift if action definitions change.
  isFeature: boolean;
}

export interface StainSpotDetectionActionSuggestions {
  stain: string;
  // Human-readable label for the stain; the UI must never render the raw stain id (e.g. `mplex`).
  stainDisplayName: string;
  suggestions: SpotDetectionActionSuggestion[];
}

const isActionPresentForStain = (actions: PostProcessingActionCreated[], actionName: string, stain: string): boolean =>
  some(actions, (action) => {
    const actionStains = action.stain ? [action.stain] : action.stains ?? [];
    return action.actionName === actionName && actionStains.includes(stain);
  });

// Suggests (rather than silently adds) the actions that make configured RNA H-score bins actually
// usable downstream. Only surfaces a stain once it has at least one RNA/spot detection selected, and
// only for actions not already present on whichever list (post-processing or features) they belong to.
export const getSuggestedSpotDetectionActionsByStain = (
  stainsWithSpotDetections: string[],
  postProcessingActionsById: Record<string, PostProcessingActionInterface>,
  postProcessingActions: PostProcessingActionCreated[],
  featureActions: PostProcessingActionCreated[],
  stainTypeIdToDisplayName: (stain: string) => string = (stain) => stain
): StainSpotDetectionActionSuggestions[] => {
  const suggestionDefinitions = compact(
    SUGGESTED_ACTION_NAMES.map((actionName) => find(postProcessingActionsById, { actionName }))
  );

  return compact(
    stainsWithSpotDetections.map((stain) => {
      const suggestions = compact(
        suggestionDefinitions.map((definition): SpotDetectionActionSuggestion | undefined => {
          const isFeature = definition.actionInput === ActionInput.FEATURE_FAMILY;
          const relevantActions = isFeature ? featureActions : postProcessingActions;
          if (isActionPresentForStain(relevantActions, definition.actionName, stain)) {
            return undefined;
          }
          return {
            actionId: definition.id,
            actionName: definition.actionName,
            actionLabel: definition.actionDisplayName,
            isFeature,
          };
        })
      );
      return suggestions.length > 0
        ? { stain, stainDisplayName: stainTypeIdToDisplayName(stain), suggestions }
        : undefined;
    })
  );
};

export interface SpotDetectionActionUpdate {
  // The existing created action's own id (not the action definition id), used to locate and patch it.
  actionId: string;
  actionName: string;
  actionLabel: string;
  isFeature: boolean;
}

export interface StainSpotDetectionActionUpdates {
  stain: string;
  stainDisplayName: string;
  updates: SpotDetectionActionUpdate[];
}

const isSameIdSet = (currentValue: unknown, expectedIds: string[]): boolean =>
  Array.isArray(currentValue) &&
  currentValue.length === expectedIds.length &&
  isEqual(sortBy(currentValue), sortBy(expectedIds));

// Detects Instance H-Score/Positivity actions that already exist for a stain but no longer match its
// current H-score bins (general or per-probe overrides) - e.g. a probe or dilation was added/removed,
// or bins were edited, after the action was configured. Never patches automatically; the caller only
// applies nextValue once the user explicitly clicks "Update" for that stain.
export const getOutOfSyncSpotDetectionActions = (
  stainsWithSpotDetections: string[],
  postProcessingActions: PostProcessingActionCreated[],
  featureActions: PostProcessingActionCreated[],
  getExpectedBinnedColumnIds: (stain: string) => string[],
  getExpectedThresholdsByInstance: (stain: string) => Record<string, number>,
  stainTypeIdToDisplayName: (stain: string) => string = (stain) => stain
): StainSpotDetectionActionUpdates[] => {
  const findActionForStain = (
    actions: PostProcessingActionCreated[],
    actionName: string,
    stain: string
  ): PostProcessingActionCreated | undefined =>
    // The index signature on PostProcessingActionCreated confuses lodash's find() overload resolution.
    find(actions, (action) => {
      const actionStains = action.stain ? [action.stain] : action.stains ?? [];
      return action.actionName === actionName && actionStains.includes(stain);
    }) as PostProcessingActionCreated | undefined;

  return compact(
    stainsWithSpotDetections.map((stain) => {
      const updates: SpotDetectionActionUpdate[] = [];

      const hScoreAction = findActionForStain(featureActions, INSTANCE_H_SCORE_ACTION_NAME, stain);
      const expectedBinnedColumnIds = getExpectedBinnedColumnIds(stain);
      if (
        hScoreAction &&
        !isEmpty(expectedBinnedColumnIds) &&
        !isSameIdSet(get(hScoreAction, INSTANCE_H_SCORE_BINNED_COLUMNS_INPUT_KEY), expectedBinnedColumnIds)
      ) {
        updates.push({
          actionId: hScoreAction.id,
          actionName: INSTANCE_H_SCORE_ACTION_NAME,
          actionLabel: 'Instance H-Score',
          isFeature: true,
        });
      }

      const positivityAction = findActionForStain(postProcessingActions, INSTANCE_COUNT_POSITIVITY_ACTION_NAME, stain);
      const expectedThresholdsByInstance = getExpectedThresholdsByInstance(stain);
      if (
        positivityAction &&
        !isEmpty(expectedThresholdsByInstance) &&
        !isEqual(get(positivityAction, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY), expectedThresholdsByInstance)
      ) {
        updates.push({
          actionId: positivityAction.id,
          actionName: INSTANCE_COUNT_POSITIVITY_ACTION_NAME,
          actionLabel: 'Instance Count Positivity By Thresholding',
          isFeature: false,
        });
      }

      return updates.length > 0 ? { stain, stainDisplayName: stainTypeIdToDisplayName(stain), updates } : undefined;
    })
  );
};
